import React, { useEffect, useState } from 'react';
import { Col, Row, Modal } from 'react-bootstrap';
import { ReactComponent as PrintIcon } from 'src/Images/icons/common/print.svg';
import PrintStamp from 'src/components/VehicleTrackingDetails/Tables/Pdf/PrintStamp';
import {
  getTargetVsActualLogs,
  getWorkflowStagesForWorkflow,
} from 'src/redux/Actions/VehicleTracking';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ReactComponent as DropdownIcon } from 'src/Images/icons/common/dropdown.svg';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import getColorCode from 'src/utils/GetThemeColor';
import Table from 'src/components/VehicleTrackingDetails/Tables/Table';
import { ReactComponent as ArrowDown } from 'src/Images/icons/common/arrowdown.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';

function TargetVsActualLogContainer({
  targetVsConfigLogs,
  themeColor,
  setSelectedLineItem,
  setShowMaterialIdentificationCard,
}) {
  const baseHeaders = [
    { label: 'ID', value: 'id' },
    { label: 'Name', value: 'name' },
    { label: 'Target', value: 'target_value' },
    { label: 'Actual', value: 'actual_value' },
    { label: 'Remark', value: 'remarks' },
  ];

  const latestLogHeaders = targetVsConfigLogs?.is_print_enabled
    ? [...baseHeaders, { label: 'Print', value: 'print' }]
    : baseHeaders;

  const historicalLogHeaders = baseHeaders;

  const latestData =
    targetVsConfigLogs.latest_logs?.line_items?.map((item) => ({
      ...item,
      time_stamp: targetVsConfigLogs.latest_logs?.time_stamp,
      id: item.object_id,
    })) ?? [];

  const historyData =
    targetVsConfigLogs.historical_logs?.map((group) => ({
      time_stamp: group.time_stamp,
      line_items: group.line_items.map((item) => ({
        ...item,
        time_stamp: group.time_stamp,
        id: item.object_id,
      })),
    })) ?? [];

  const renderTableContent = (data, headers) => (
    <tbody>
      {data?.length > 0 ? (
        data.map((lineItem) => (
          <tr key={lineItem.id}>
            {headers.map((header) => (
              <td className="table-data" key={`${lineItem.id}-${header.value}`}>
                <div className="div-flex-row">
                  {header.value === 'print' ? (
                    <PrintIcon
                      color={themeColor}
                      width={30}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedLineItem(lineItem);
                        setShowMaterialIdentificationCard(true);
                      }}
                    />
                  ) : (
                    <span className="text-sm">
                      {lineItem[header.value] ?? ''}
                    </span>
                  )}
                </div>
              </td>
            ))}
          </tr>
        ))
      ) : (
        <tr>
          <td
            colSpan={headers.length}
            className="no-data-found-message border-bottom"
          >
            <span className="text-sm">No Vehicle Data Found</span>
          </td>
        </tr>
      )}
    </tbody>
  );

  const [showTable, setShowTable] = useState(false);

  return (
    <div className="div-flex-column rg-10 padding-left-right">
      <div className="target-vs-actual-log-container">
        <div className="target-vs-actual-log-header text-sm text-medium-bold border-bottom">
          {targetVsConfigLogs.title}
        </div>
        <div className="target-vs-actual-log-content div-flex-column rg-10">
          <div>
            <span className="text-md text-medium-bold">Assigned Table:</span>
            <span className="text-grey left-padding">
              {targetVsConfigLogs.table_name}
            </span>
          </div>
          <div>
            <span className="text-sm text-medium-bold">Assigned Field:</span>
            <span className="text-grey left-padding">
              {targetVsConfigLogs.field_name}
            </span>
          </div>
        </div>
      </div>

      <div
        className={`table-fixed-container theme-horizontal-scrollable-container-${themeColor}`}
      >
        <Table
          headers={latestLogHeaders}
          minCellWidth={150}
          tableContent={renderTableContent(latestData, latestLogHeaders)}
        />
      </div>

      {historyData.length > 0 && (
        <div>
          <div className="target-vs-actual-log-container target-vs-actual-log-header div-flex-row div-align-center div-space-between border-bottom">
            <span className="text-md text-medium-bold">Activity Log</span>
            <button
              className="btn btn-sm cursor-pointer"
              onClick={() => setShowTable(!showTable)}
              aria-label="Toggle activity log"
            >
              <ArrowDown
                className={`icon-size-md ${showTable ? 'rotate-icon-180' : ''}`}
                color="#9F9F9F"
              />
            </button>
          </div>

          {showTable &&
            historyData.map((group) => (
              <div
                className="div-flex-column rg-10 padding-left-right"
                key={group.time_stamp}
              >
                <div className="padding-top-10">
                  <Calendar
                    className="icon-size-sm"
                    color={getColorCode(themeColor)}
                  />
                  <span className="text-grey left-padding">
                    {group.time_stamp}
                  </span>
                </div>
                <div
                  className={`table-fixed-container theme-horizontal-scrollable-container-${themeColor}`}
                >
                  <Table
                    headers={historicalLogHeaders}
                    minCellWidth={150}
                    tableContent={renderTableContent(
                      group.line_items,
                      historicalLogHeaders,
                    )}
                  />
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function AdditionalDetails() {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const vehicleTrackingDataReducer = useSelector(
    (state) => state?.VehicleTrackingReducer,
  );
  const { targetVsActualLogsList, vehicleTrackingDetails } =
    vehicleTrackingDataReducer;
  const { workflowStagesForWorkflow } = vehicleTrackingDataReducer;

  const { workflowStageId, vehicleTrackingNumber } = useParams();
  const [showMaterialIdentificationCard, setShowMaterialIdentificationCard] =
    useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);

  useEffect(() => {
    dispatch(
      getWorkflowStagesForWorkflow(
        tenantOrganisationId,
        vehicleTrackingDetails?.workflowId,
      ),
    );
  }, [dispatch, tenantOrganisationId, vehicleTrackingDetails?.workflowId]);

  useEffect(
    () => () => {
      dispatch({ type: 'RESET_PRINT_DETAILS' });
    },
    [],
  );
  useEffect(() => {
    if (selectedWorkflowId === null) return;
    dispatch(
      getTargetVsActualLogs(
        tenantOrganisationId,
        vehicleTrackingNumber,
        selectedWorkflowId,
      ),
    );
  }, [selectedWorkflowId]);

  useEffect(() => {
    if (workflowStagesForWorkflow.length > 0) {
      setSelectedWorkflowId(Number(workflowStageId));
    }
  }, [workflowStagesForWorkflow]);

  const workflowOptions = workflowStagesForWorkflow.map((workflowObj) => ({
    label: workflowObj.name,
    value: workflowObj.id,
  }));

  return (
    <>
      <Row className="padding-left-right">
        <div className="div-flex-row div-space-between section-title div-align-center">
          <Col xxl={4} xl={4} lg={4} md={4} sm={1}>
            <div className="text-md text-medium-bold">Activity Logs</div>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={4} sm={1}>
            <span className="text-sm text-grey text-medium-bold">
              Select another workflow stage
            </span>
            <div className="input-wrapper">
              <CustomMultiSelect
                name="workflowId"
                isMulti={false}
                className="multi-select-input-section"
                onChange={(selectedOption) => {
                  setSelectedWorkflowId(selectedOption?.value || null);
                }}
                value={workflowOptions.find(
                  (option) => option.value === selectedWorkflowId,
                )}
                options={workflowOptions}
              />
              <span className="form-input-section-icon">
                <DropdownIcon
                  className="icon-size-sm"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
      </Row>

      <div className="div-flex-column rg-20">
        {targetVsActualLogsList?.map((targetVsConfigLogs) => (
          <TargetVsActualLogContainer
            key={`${targetVsConfigLogs.table_name}-${targetVsConfigLogs.field_name}`}
            targetVsConfigLogs={targetVsConfigLogs}
            themeColor={themeColor}
            selectedWorkflowId={selectedWorkflowId}
            targetVsActualLogsList={targetVsActualLogsList}
            setSelectedLineItem={setSelectedLineItem}
            setShowMaterialIdentificationCard={
              setShowMaterialIdentificationCard
            }
          />
        ))}
      </div>

      <Modal
        show={showMaterialIdentificationCard}
        backdrop="static"
        keyboard={false}
        centered
        size="xl"
        onHide={() => setShowMaterialIdentificationCard(false)}
      >
        <div className="div-flex-column">
          <div className="div-flex-column rg-10">
            <div id="quotation-content">
              <PrintStamp
                setShowMaterialIdentificationCard={
                  setShowMaterialIdentificationCard
                }
                selectedLineItem={selectedLineItem}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default AdditionalDetails;
